import DeleteIcon from '@mui/icons-material/Delete'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import axios from 'axios'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { BackButton } from '~/src/components/atoms/Button/BackButton/BackButton'
import { ProtectedPage } from '~/src/components/templates'
import { useMessage } from '~/src/hooks'
import { useUser } from '~/src/hooks/useApi'
import { userRepository } from '~/src/repositories'
import { TDestinationFormValues, TProfileFormValues } from '~/src/types/form'
import { DestinationForm } from './components/DestinationForm/DestinationForm'
import { ProfileForm } from './components/ProfileForm/ProfileForm'
import { LinkButton } from '../../atoms/Button/LinkButton/LinkButton'

export const CustomerDetail: React.FC = (): JSX.Element => {
  const { t } = useTranslation()
  const { userId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { data: user, mutate } = useUser(Number(userId))

  const [isLoadingDelete, setIsLoadingDelete] = useState(false)
  const [isLoadingSave, setIsLoadingSave] = useState(false)
  const [isLoadingSaveDestination, setIsLoadingSaveDestination] = useState(false)
  const { handleOpenWithSuccessMessage, handleOpenWithErrorMessage } = useMessage()

  const handleSaveUserProfile = async (formValue: TProfileFormValues) => {
    try {
      setIsLoadingSave(true)
      await userRepository.patch(Number(userId), {
        ...formValue,
        isSigned: Boolean(formValue.isSigned),
        canSearchGenuinePart: Boolean(formValue.canSearchGenuinePart),
      })
      handleOpenWithSuccessMessage(t('common.success_message'))
      mutate()
    } catch (e) {
      if (axios.isAxiosError(e)) {
        handleOpenWithErrorMessage(e.message)
      }
    } finally {
      setIsLoadingSave(false)
    }
  }

  const handleSaveDestination = async (
    id: number,
    formValue: TDestinationFormValues
  ): Promise<void> => {
    try {
      setIsLoadingSaveDestination(true)
      await userRepository.patch(Number(userId), {
        ...formValue,
        destinationId: id,
      })
      mutate()
    } catch (e) {
      if (axios.isAxiosError(e)) {
        handleOpenWithErrorMessage(e.message)
      }
    } finally {
      setIsLoadingSaveDestination(false)
    }
  }

  const handleDeleteMember = async () => {
    try {
      setIsLoadingDelete(true)
      await userRepository.delete(Number(userId))
      navigate('/admin/member/list')
    } catch (e) {
      if (axios.isAxiosError(e)) {
        handleOpenWithErrorMessage(e.message)
      }
    } finally {
      setIsLoadingDelete(false)
    }
  }

  return (
    <ProtectedPage pageTitle={t('user_detail.member_details')}>
      <Box minWidth={700}>
        {/*
        会員削除についての仕様の詳細が決定したら実装
        <Box display={'flex'} justifyContent={'flex-end'} sx={{ mb: '36px' }}>
          <Button
            variant="outlined"
            sx={{ color: '#F44336', borderColor: '#F44336' }}
            onClick={handleDeleteMember}
            disabled={isLoadingDelete}
          >
            <DeleteIcon fontSize={'small'} sx={{ color: '#F44336' }} />
            <Typography
              sx={{
                marginLeft: '12px',
                fontStyle: 'normal',
                fontSize: 14,
                lineHeight: '24px',
              }}
            >
              {t('user_detail.delete')}
            </Typography>
          </Button>
        </Box>
        */}
        {!user ? <CircularProgress /> : null}
        {user && (
          <>
            <Box display={"flex"} alignItems={"center"} justifyContent={"space-between"}>
              <BackButton location={location} />
              <LinkButton
                text={t('common.quotation_detail')}
                handleLink={() => 
                  navigate(`/admin/member/quotation/${userId}`, {
                    replace: true,
                  })
                }
              />
            </Box>
            <ProfileForm
              data={user.user}
              onSave={handleSaveUserProfile}
              isLoadingSave={isLoadingSave}
            />
          </>
        )}
        {user?.destinations.map((d, i) => (
          <DestinationForm
            key={`destination-form-${i}`}
            title={t(`user_detail.destination_${i + 1}`)}
            id={d.id}
            onSave={handleSaveDestination}
            data={d}
            isLoading={isLoadingSaveDestination}
          />
        ))}
      </Box>
    </ProtectedPage>
  )
}
